import {
  COOKIE_PREFERENCES_LABEL,
  listSocialLinks,
  SOCIAL_NETWORK_LABELS,
  STOREFRONT_FRAME_COPY,
  STOREFRONT_POLICY_TITLES,
  storefrontFooterLegal,
  storefrontSearchLabel,
} from "@loot-os/api-contract";
import { SocialIcon } from "@loot-os/design-system/core/SocialIcon";
import type { StoreChrome as StoreChromeContent } from "@loot-os/design-system/templates/shared/content";
import { StoreChrome } from "@loot-os/design-system/templates/shared/StoreChrome";
import chatIcon from "@loot-os/icons/icons/chat";
import homeIcon from "@loot-os/icons/icons/home";
import { useLocation, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { spendableCents } from "../lib/balance.js";
import { setChatOpen } from "../lib/chat.js";
import { setDepositOpen } from "../lib/deposit.js";
import { useFooterTear } from "../lib/footerTear.js";
import { legalParties } from "../lib/legal.js";
import { type NavLink, navLinksIn, visibleNavItems } from "../lib/nav.js";
import { useBalance, useShopper } from "../lib/shopper.js";
import { socialLinksOf } from "../lib/socialLinks.js";
import { useTenant } from "../lib/tenant.js";
import { useTerminology } from "../lib/terminology.js";
import { termlyOf } from "../lib/termly.js";
import { AccountMenu } from "./AccountMenu.js";
import { CookiePreferencesButton } from "./CookiePreferencesButton.js";
import { StoreChatLauncher } from "./chat/StoreChatLauncher.js";
import { NotificationBell } from "./notifications/NotificationBell.js";

/**
 * The store's frame around every page: the design system's StoreChrome, filled from the tenant's
 * config and the shopper. Each bar shows the links placed in it (`STOREFRONT_NAV_LINKS`' `places`); a phone's
 * tab bar adds Home before them and chat (when the store has it) after; on a wide screen the
 * header's chat launcher opens it instead. Money is the spendable
 * balance; it and Deposit open Add funds (`lib/deposit.ts`) over the page.
 */
export function StoreFrame({ chat, children }: { chat: boolean; children: ReactNode }) {
  const { user } = useShopper();
  const balance = useBalance().data ?? null;
  const { config, hasFeature } = useTenant();
  const terms = useTerminology();
  const footerTear = useFooterTear();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const links = visibleNavItems(hasFeature, terms);
  const { legalName, year } = legalParties(config);
  const current = (link: NavLink) =>
    link.exact ? pathname === link.to : pathname === link.to || pathname.startsWith(`${link.to}/`);

  const chrome: StoreChromeContent = {
    name: config.name,
    logoSrc: config.branding.logoUrl || undefined,
    links: navLinksIn(links, "header").map((link) => ({
      id: link.to,
      label: link.label,
      icon: link.icon,
      href: link.to,
      current: current(link),
    })),
    mobileTabs: [
      {
        id: "home",
        label: STOREFRONT_FRAME_COPY.home,
        icon: homeIcon,
        href: "/",
        current: pathname === "/",
      },
      ...navLinksIn(links, "tabs").map((link) => ({
        id: link.to,
        label: link.label,
        icon: link.icon,
        href: link.to,
        current: current(link),
      })),
      ...(chat
        ? [
            {
              id: "chat",
              label: STOREFRONT_FRAME_COPY.chat,
              icon: chatIcon,
              render: <button type="button" onClick={() => setChatOpen(true)} />,
            },
          ]
        : []),
    ],
    searchLabel: storefrontSearchLabel(terms),
    onSearch: (q) => void navigate({ to: "/boxes", search: q ? { q } : {} }),
    balance: user && balance ? spendableCents(balance) : undefined,
    onBalance: () => setDepositOpen(true),
    deposit: hasFeature("balancePurchases")
      ? { label: STOREFRONT_FRAME_COPY.deposit, onClick: () => setDepositOpen(true) }
      : undefined,
    signIn: user ? undefined : { label: STOREFRONT_FRAME_COPY.logIn, href: "/login" },
    actions:
      chat || user ? (
        <>
          {chat && <StoreChatLauncher />}
          {user && (
            <>
              <NotificationBell />
              <AccountMenu />
            </>
          )}
        </>
      ) : undefined,
    footer: {
      socials: listSocialLinks(socialLinksOf(config)).map(({ network, url }) => ({
        label: SOCIAL_NETWORK_LABELS[network],
        href: url,
        icon: <SocialIcon network={network} className="size-4" />,
      })),
      columns: [
        {
          title: STOREFRONT_FRAME_COPY.storeColumn,
          links: navLinksIn(links, "footer").map((link) => ({ label: link.label, href: link.to })),
        },
        {
          title: STOREFRONT_FRAME_COPY.legalColumn,
          links: [
            { label: STOREFRONT_POLICY_TITLES.terms, href: "/terms" },
            { label: STOREFRONT_POLICY_TITLES.privacy, href: "/privacy" },
            ...(termlyOf(config).websiteUuid
              ? [
                  {
                    label: COOKIE_PREFERENCES_LABEL,
                    render: <CookiePreferencesButton />,
                  },
                ]
              : []),
          ],
        },
      ],
      legal: storefrontFooterLegal({ year, legalName }),
    },
    footerTear,
  };

  return <StoreChrome chrome={chrome}>{children}</StoreChrome>;
}
